import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { gsap } from 'gsap';
import {
  GamepadIcon,
  SettingsIcon,
  LogOutIcon,
  UserIcon,
  PaletteIcon,
  ChevronRightIcon,
  XIcon
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { roleToCompany, getCompanyDisplayName } from '../../utils/roleUtils';

interface SidebarProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, setIsOpen }) => {
  const { user, logout } = useAuth0();
  const { isDark, toggleTheme } = useTheme();
  const { roles } = usePermissions();
  const location = useLocation();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const menuItemsRef = useRef<HTMLDivElement[]>([]);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return;

    const isDesktop = window.innerWidth >= 1024;

    if (isDesktop) {
      // On desktop, always show sidebar
      gsap.set(sidebar, { x: '0%', visibility: 'visible' });
      
      // Animate menu items on first load
      gsap.fromTo(menuItemsRef.current, 
        { x: -50, opacity: 0 },
        { 
          x: 0, 
          opacity: 1, 
          duration: 0.3, 
          stagger: 0.1, 
          delay: 0.2,
          ease: 'power2.out'
        }
      );
    } else {
      // Mobile behavior
      if (isOpen) {
        gsap.set(sidebar, { x: '-100%', visibility: 'visible' });
        gsap.to(sidebar, {
          x: '0%',
          duration: 0.4,
          ease: 'power2.out'
        });

        // Animate menu items
        gsap.fromTo(menuItemsRef.current, 
          { x: -50, opacity: 0 },
          { 
            x: 0, 
            opacity: 1, 
            duration: 0.3, 
            stagger: 0.1, 
            delay: 0.2,
            ease: 'power2.out'
          }
        );
      } else {
        gsap.to(sidebar, {
          x: '-100%',
          duration: 0.3,
          ease: 'power2.in',
          onComplete: () => {
            gsap.set(sidebar, { visibility: 'hidden' });
          }
        });
      }
    }
  }, [isOpen]);

  // Function to convert role to company name and display label
  const roleToCompanyInfo = (role: string) => {
    const company = roleToCompany(role);
    const displayName = getCompanyDisplayName(company);
    return {
      company,
      displayName: `${displayName} Games`
    };
  };

  // Generate menu items dynamically from user roles
  const menuItems = roles.map(role => {
    const { company, displayName } = roleToCompanyInfo(role);
    const path = `/games/${company}`;
    return {
      id: `games-${company}`,
      path,
      icon: GamepadIcon,
      label: displayName,
      isActive: location.pathname === path || location.pathname.startsWith(`/games/${company}`)
    };
  });

  // No fallback - only show menu items for roles the user actually has
  // If user has no roles, they will see an empty sidebar (which is correct)

  const settingsItems = [
    {
      id: 'theme',
      action: toggleTheme,
      icon: PaletteIcon,
      label: isDark ? 'Light Mode' : 'Dark Mode'
    },
    {
      id: 'profile',
      path: '/settings/profile',
      icon: UserIcon,
      label: 'User Profile'
    },
    {
      id: 'logout',
      action: () => logout({ logoutParams: { returnTo: window.location.origin } }),
      icon: LogOutIcon,
      label: 'Logout'
    }
  ];

  const handleItemClick = (item: any) => {
    if (item.action) {
      item.action();
    }
    if (window.innerWidth < 1024) {
      setIsOpen(false);
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className="fixed left-0 top-0 h-full w-64 bg-white dark:bg-dark-800 border-r border-gray-200 dark:border-dark-700 z-50 lg:static lg:translate-x-0 lg:visible"
        style={{ visibility: 'hidden' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-dark-700">
          <div className="flex items-center gap-3">
            <div className="w-8 h-6 bg-primary-600 rounded-lg flex items-center justify-center">
              <GamepadIcon className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              Games Admin
            </h1>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="lg:hidden p-1 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-md transition-colors"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* User info */}
        <div className="p-6 border-b border-gray-200 dark:border-dark-700">
          <div className="flex items-center gap-3">
            <img
              src={user?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'User')}&background=3b82f6&color=fff`}
              alt={user?.name}
              className="w-10 h-10 rounded-full"
            />
            <div>
              <p className="font-medium text-gray-900 dark:text-gray-100">
                {user?.name}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {user?.email}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-6 space-y-2">
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <div
                key={item.id}
                ref={el => menuItemsRef.current[index] = el!}
              >
                <Link
                  to={item.path}
                  onClick={() => handleItemClick(item)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 group ${
                    item.isActive
                      ? 'bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              </div>
            );
          })}

          {/* Settings */}
          <div
            ref={el => menuItemsRef.current[menuItems.length] = el!}
            className="mt-6"
          >
            <button
              onClick={() => setSettingsOpen(!settingsOpen)}
              className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700 transition-all duration-200"
            >
              <div className="flex items-center gap-3">
                <SettingsIcon className="w-5 h-5" />
                <span className="font-medium">Settings</span>
              </div>
              <ChevronRightIcon 
                className={`w-4 h-4 transition-transform duration-200 ${
                  settingsOpen ? 'rotate-90' : ''
                }`} 
              />
            </button>
            
            {settingsOpen && (
              <div className="mt-2 ml-8 space-y-1">
                {settingsItems.map((item) => {
                  const Icon = item.icon;
                  
                  if (item.path) {
                    return (
                      <Link
                        key={item.id}
                        to={item.path}
                        onClick={() => handleItemClick(item)}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700 transition-all duration-200"
                      >
                        <Icon className="w-4 h-4" />
                        <span>{item.label}</span>
                      </Link>
                    );
                  }

                  return (
                    <button
                      key={item.id}
                      onClick={() => handleItemClick(item)}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700 transition-all duration-200"
                    >
                      <Icon className="w-4 h-4" />
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </nav>
      </div>
    </>
  );
};

export default Sidebar;
